/**
 * Corroboration — existing diagnostic signals, read as EVIDENCE for a situation
 * declaration, never as a determination.
 *
 * The product already computes most of what a facilitator reads in the room,
 * after the fact: Fracture findings (hidden_dissent, knife_edge) on prior
 * sessions, participant-raised fairness concerns, decisions that superseded a
 * previous one. This module turns those into prompts — "should candour be set
 * to 'not reliably'?" — that the facilitator confirms or declines.
 *
 * Rules that matter more than the wiring (PRD §5 Phase 4):
 *   - Aggregate only. Counts by type. No signal is ever attributed to a named
 *     participant here; `raised_by` is never selected.
 *   - Suppress below a threshold. With fewer than CORROBORATION_PARTICIPANT_THRESHOLD
 *     participants a "buried minority" is one identifiable person, so no prompt
 *     is shown at all.
 *   - The facilitator confirms. A prompt opens a question; it never writes the
 *     vector. Declining is remembered per session (`dismissed`).
 */

import type { SituationDimension, SituationVector } from '../types/prioritization/situationVector';
import { getTypedSupabaseClient } from './typedSupabaseClient';

export const CORROBORATION_PARTICIPANT_THRESHOLD = 4;
/** How far back workspace signals are counted. */
export const CORROBORATION_WINDOW_DAYS = 90;

export interface SituationSignals {
  /** Active Fracture findings of type hidden_dissent in the workspace. */
  hiddenDissentFindings: number;
  /** Active Fracture findings of type knife_edge in the workspace. */
  knifeEdgeFindings: number;
  /** Open fairness concerns of type not_heard. */
  openNotHeardFlags: number;
  /** Open fairness concerns of type vote_suppressed. */
  openVoteSuppressedFlags: number;
  /** Decisions in the window that superseded an earlier decision. */
  supersededDecisions: number;
}

export const EMPTY_SITUATION_SIGNALS: SituationSignals = {
  hiddenDissentFindings: 0,
  knifeEdgeFindings: 0,
  openNotHeardFlags: 0,
  openVoteSuppressedFlags: 0,
  supersededDecisions: 0,
};

export interface CorroborationPrompt {
  /** Stable key, used to remember a decline: `${dimension}:${suggestedValue}`. */
  key: string;
  dimension: SituationDimension;
  suggestedValue: string;
  /** The question put to the facilitator. Counts only, never names. */
  text: string;
}

function plural(n: number, singular: string, pluralForm = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

/** Pure. Which prompts to show for these signals, this vector, and this room size. */
export function deriveCorroborationPrompts(
  signals: SituationSignals,
  opts: { participantCount: number; vector: SituationVector | null | undefined; dismissed?: readonly string[] },
): CorroborationPrompt[] {
  if (opts.participantCount < CORROBORATION_PARTICIPANT_THRESHOLD) return [];
  const vector = opts.vector ?? {};
  const dismissed = new Set(opts.dismissed ?? []);
  const prompts: CorroborationPrompt[] = [];

  if (signals.hiddenDissentFindings > 0 && vector.candour !== 'guarded') {
    prompts.push({
      key: 'candour:guarded',
      dimension: 'candour',
      suggestedValue: 'guarded',
      text: `Recent sessions in this workspace surfaced buried opposition (${plural(signals.hiddenDissentFindings, 'hidden-dissent finding')}). Should candour be set to "Not reliably"?`,
    });
  }

  const unheard = signals.openNotHeardFlags + signals.openVoteSuppressedFlags;
  if (unheard > 0 && vector.voice !== 'anchored') {
    const parts: string[] = [];
    if (signals.openNotHeardFlags > 0) parts.push(plural(signals.openNotHeardFlags, '"not heard" concern'));
    if (signals.openVoteSuppressedFlags > 0) parts.push(plural(signals.openVoteSuppressedFlags, '"vote suppressed" concern'));
    prompts.push({
      key: 'voice:anchored',
      dimension: 'voice',
      suggestedValue: 'anchored',
      text: `There are open fairness concerns in this workspace (${parts.join(', ')}). Is there a voice that sets the frame before others speak?`,
    });
  }

  if (signals.supersededDecisions > 0 && vector.stability !== 'reversing') {
    prompts.push({
      key: 'stability:reversing',
      dimension: 'stability',
      suggestedValue: 'reversing',
      text: `${plural(signals.supersededDecisions, 'decision')} in the last ${CORROBORATION_WINDOW_DAYS} days superseded an earlier one. Has this group reversed a decision of this kind recently?`,
    });
  }

  if (signals.knifeEdgeFindings > 0 && vector.altitude !== 'criteria_contested') {
    prompts.push({
      key: 'altitude:criteria_contested',
      dimension: 'altitude',
      suggestedValue: 'criteria_contested',
      text: `Recent rankings sat on a knife edge (${plural(signals.knifeEdgeFindings, 'finding')}): a small weight change flips the order. That often means the disagreement is about the criteria. Do you disagree on what matters?`,
    });
  }

  return prompts.filter((p) => !dismissed.has(p.key));
}

async function count(query: PromiseLike<{ count: number | null; error: { message: string } | null }>): Promise<number> {
  const { count: n, error } = await query;
  if (error) {
    console.warn('[situationCorroboration] count failed:', error.message);
    return 0;
  }
  return n ?? 0;
}

/** Aggregate counts only. Fails soft (a failed count reads as 0) — corroboration is evidence, not a gate. */
export async function fetchSituationSignals(workspaceId: string): Promise<SituationSignals> {
  const db = getTypedSupabaseClient();
  const since = new Date(Date.now() - CORROBORATION_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();

  const findings = (type: string) =>
    count(
      db
        .from('agent_findings')
        .select('id', { count: 'exact', head: true })
        .eq('workspace_id', workspaceId)
        .eq('status', 'active')
        .eq('finding_type', type),
    );
  const flags = (concern: string) =>
    count(
      db
        .from('session_fairness_flags')
        .select('id', { count: 'exact', head: true })
        .eq('workspace_id', workspaceId)
        .eq('status', 'open')
        .eq('concern_type', concern),
    );

  const [hiddenDissentFindings, knifeEdgeFindings, openNotHeardFlags, openVoteSuppressedFlags, supersededDecisions] =
    await Promise.all([
      findings('hidden_dissent'),
      findings('knife_edge'),
      flags('not_heard'),
      flags('vote_suppressed'),
      count(
        db
          .from('decisions')
          .select('id', { count: 'exact', head: true })
          .eq('workspace_id', workspaceId)
          .not('supersedes_decision_id', 'is', null)
          .gte('created_at', since),
      ),
    ]);

  return { hiddenDissentFindings, knifeEdgeFindings, openNotHeardFlags, openVoteSuppressedFlags, supersededDecisions };
}
